import { Box, Button } from '@material-ui/core';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import MyLocation from '@material-ui/icons/MyLocation';
import Save from '@material-ui/icons/Save';
import produce from 'immer';
import {
	MapViewer,
	MapViewerControls,
	MapAttachmentEditDialog,
	ProjectEditDialog,
	AssignmentDialog,
	IProjectEditDialogProps
} from '..';
import {
	FeatureFlags,
	MapDetailDto,
	ProjectDto,
	UserGroupDto
} from '../../../models';
import {
	Project,
	Attachment,
	LegendItem,
	legendStringToLegendItems,
	AppState,
	MapViewerPanel,
	AppThunkDispatch,
	MapAction,
	ProjectActions,
	projectAddAttachment,
	projectUpdateAttachments,
	projectMoveAttachment,
	projectCreate,
	projectUpdate,
	Bundle,
	projectUpdateAttachment,
	mapViewerClearActionPanels,
	MapViewerActions,
	projectRemoveAttachment,
	mapViewerAddToLastViewed,
	HttpAction,
	mapViewerSwitchActionPanel,
	projectUndo,
	projectRedo,
	searchSetPreserveState,
	searchClear,
	projectCopy,
	shareProjectWithGroup,
	fetchBosatlas
} from '../../../redux';
import {
	convertToLatLng,
	extractPrefix,
	getMapTitle,
	isAppleDevice
} from '../../../utils';
import { LoadingContainer, Header5, ErrorBoundary, Header6 } from '../../atoms';
import {
	BackButton,
	MapDrawing,
	MapAttachementSidePanel,
	AttachmentLocationChanger,
	GoogleMapsViewer,
	UnsavedProjectDialog,
	ProjectMoreOptions
} from '../../molecules';
import {
	useBrandName,
	useCallbackSafeProps,
	useIsOwner,
	useIsSafeExamBrowser
} from '../../../hooks';
import { useHistory } from 'react-router';
import { config } from '../../../config';
import { LatLng } from 'leaflet';
import { LeafletIconPin } from '../../atoms/leaflet-icon-pin/leaflet-icon-pin.component';
import { Tooltip as LeafletTooltip } from 'react-leaflet';
import {
	IShareDialogProps,
	ShareDialog
} from '../share-dialog/share-dialog.component';

import './project-viewer.styles.scss';
import { useTranslation } from 'react-i18next';
import { ContentFeedback } from '../../molecules/content-feedback/content-feedback.component';
import { Metadata } from '@infinitaslearning/module-il-feedback-widget';
import { useFeatureFlag } from 'configcat-react';
export interface IProjectViewerProps {
	project: Project;
	map: MapDetailDto;
	topographicLocation?: LatLng;
	topographicName?: string;
}

export interface IProjectViewerStateProps {
	showControls: boolean;
	showAttachmentPanel: boolean;
	googleMapsActive: boolean;
	isProjectDirty: boolean;
	sharedProjects?: ProjectDto[];
	addAttachment: (attachment: Attachment, file?: File) => void;
	removeAttachment: (attachment: Attachment) => void;
	updateAttachment: (attachment: Attachment, file?: File) => void;
	updateAttachments: (attachments: Attachment[]) => void;
	moveAttachment: (attachment: Attachment) => void;
	createProject: (project: Project, brandName: string) => void;
	copyProject: (project: Project, brandName: string) => void;
	updateProject: (project: Project) => void;
	clearActionPanels: () => void;
	addToLastViewed: (mapId: string, mapUrl: string, title: string) => void;
	toggleGoogleMaps: () => void;
	undo: () => void;
	redo: () => void;
	setPreserveSearchState: () => void;
	clearSearchState: () => void;
	shareProject: (groups: UserGroupDto[], project: Project) => void;
	fetchBosatlas: () => void;
}

function ProjectViewerView(
	props: IProjectViewerProps & IProjectViewerStateProps
): ReactElement {
	const {
		project,
		map,
		topographicLocation,
		topographicName,
		showControls,
		showAttachmentPanel,
		googleMapsActive,
		isProjectDirty,
		sharedProjects,
		addAttachment,
		removeAttachment,
		updateAttachment,
		updateAttachments,
		moveAttachment,
		createProject,
		copyProject,
		updateProject,
		clearActionPanels,
		addToLastViewed,
		toggleGoogleMaps,
		undo,
		redo,
		setPreserveSearchState,
		clearSearchState,
		shareProject,
		fetchBosatlas
	} = props;

	const { t } = useTranslation(['project', 'general']);
	const history = useHistory();
	const isSafeExamBrowser = useIsSafeExamBrowser();
	const [saveSharedProject, setSaveSharedProject] = useState<boolean>(false);
	const [getSafeProject] = useCallbackSafeProps(project);

	const [saveDialogState, setSaveDialogState] =
		useState<IProjectEditDialogProps>({
			open: false,
			dialogTitle: '',
			title: '',
			message: '',
			entityName: 'map',
			bundles: [],
			onClose: closeSaveDialog,
			onSave: (_: string, __: Bundle[]) => {}
		});

	const [shareDialogState, setShareDialogState] = useState<IShareDialogProps>(
		{
			open: false,
			objectDescriptor: 'map',
			onClose: closeShareDialog,
			onSave: openSaveDialog,
			onShare: (groups: UserGroupDto[], project: Project) => {
				shareProject(groups, project);
			}
		}
	);

	const [assignmentDialogOpen, setAssignmentDialogOpen] =
		useState<boolean>(false);
	const brandName: string = useBrandName();
	const isProjectOwner: boolean = useIsOwner(project);
	const { value: isFeedbackButtonVisible } = useFeatureFlag(
		FeatureFlags.feedback,
		false
	);

	const legendItems: LegendItem[] | undefined = useMemo<
		LegendItem[] | undefined
	>(() => {
		return map.legend ? legendStringToLegendItems(map.legend) : undefined;
	}, [map]);

	const metadata: Metadata = useMemo(() => {
		return {
			atlasMapId: map.id,
			atlasMapTitle: map.title,
			atlasCategory: map.category,
			atlasChapter: map.chapter
		};
	}, [map]);

	const attachmentsWithLocation: Attachment[] = useMemo<Attachment[]>(() => {
		return project.attachments.filter(
			(attachment: Attachment) => attachment.pointOnMap !== undefined
		);
	}, [project.attachments]);

	const projectTitle: string = useMemo<string>(() => {
		const showMapTitle: boolean =
			project.id === undefined || !project.title;
		return (
			showMapTitle && map ? getMapTitle(map) : project.title
		) as string;
	}, [map, project]);

	const showTopographicPin: boolean = useMemo<boolean>(() => {
		return (
			!googleMapsActive &&
			topographicLocation !== undefined &&
			topographicName !== undefined
		);
	}, [googleMapsActive, topographicLocation, topographicName]);

	const isSharedProject: boolean | undefined = useMemo<
		boolean | undefined
	>(() => {
		return sharedProjects?.some(
			(sharedProject) => sharedProject.id === project.id
		);
	}, [sharedProjects, project]);

	useEffect(() => {
		if (!project.id) {
			addToLastViewed(map.id, map.thumbnailUrl as string, projectTitle);
		}
		clearActionPanels();
		setupShortcuts();
		return () => {
			removeShortcuts();
			if (history.action === 'POP') setPreserveSearchState();
			clearSearchState();
		};
	}, []);

	useEffect(() => {
		if (!sharedProjects) fetchBosatlas();
	}, [sharedProjects]);

	function shouldShowUnsavedChangesDialog(): boolean {
		if (!isProjectDirty || !isProjectOwner || project.id !== undefined)
			return false;

		return (
			window.localStorage.getItem(
				config.showUnsavedProjectChangesDialogLocalstorageKey
			) !== 'false'
		);
	}

	function openShareDialog(): void {
		setShareDialogState({
			...shareDialogState,
			open: !shareDialogState.open
		});
	}

	function closeShareDialog(): void {
		setShareDialogState({ ...shareDialogState, open: false });
	}

	function setupShortcuts(): void {
		window.addEventListener('keydown', handleKeyPress);
	}

	function removeShortcuts(): void {
		window.removeEventListener('keydown', handleKeyPress);
	}

	function handleKeyPress(event: KeyboardEvent): void {
		const actionUndo: boolean =
			(event.ctrlKey || (isAppleDevice() && event.metaKey)) &&
			event.key === 'z';
		const actionRedo: boolean =
			(event.ctrlKey || (isAppleDevice() && event.metaKey)) &&
			event.key === 'y';

		if (actionUndo) {
			undo();
			event.preventDefault();
		}

		if (actionRedo) {
			redo();
			event.preventDefault();
		}
	}

	function removePinLocation(attachment: Attachment): void {
		if (!project) return;

		const newAttachment: Attachment = produce(
			attachment,
			(draft: Attachment) => {
				draft.pointOnMap = undefined;
			}
		);

		updateAttachment(newAttachment);
	}

	function openSaveDialog(): void {
		if (isSharedProject) {
			setSaveSharedProject(true);
			return;
		}

		setSaveDialogState({
			...saveDialogState,
			title: projectTitle,
			dialogTitle: t('project:saveMap'),
			message: t('project:saveMapText', { brandName }),
			bundles: project.bundles,
			open: true,
			onSave: saveProject
		});
	}

	function openSaveAsDialog(): void {
		setSaveDialogState({
			...saveDialogState,
			title: projectTitle,
			dialogTitle: t('project:saveMapAs'),
			message: t('project:saveMapAsText', { brandName }),
			bundles: [],
			open: true,
			onSave: saveProjectAs
		});
	}

	function closeSaveDialog(): void {
		setSaveDialogState({ ...saveDialogState, open: false });
	}

	function saveProject(title: string, bundles: Bundle[]) {
		const projectToSave: Project = {
			...getSafeProject(),
			title,
			bundles
		};

		if (project.id) {
			updateProject(projectToSave);
		} else {
			createProject(projectToSave, brandName);
		}
	}

	function saveProjectAs(title: string, bundles: Bundle[]): void {
		const projectToSave: Project = {
			...getSafeProject(),
			title,
			bundles
		};

		isProjectOwner
			? createProject(projectToSave, brandName)
			: copyProject(projectToSave, brandName);

		closeSaveDialog();
	}

	function handleBackClick(): void {
		if (googleMapsActive) toggleGoogleMaps();
		else {
			setPreserveSearchState();
			history.go(-1);
		}
	}

	return (
		<Box className="project-viewer">
			{(shouldShowUnsavedChangesDialog() || saveSharedProject) && (
				<UnsavedProjectDialog
					isDirty={isProjectDirty}
					hasId={project.id !== undefined}
					onSave={saveProject}
					title={projectTitle}
					bundles={project.bundles}
					entityName="map"
					typeOfSave={
						saveSharedProject ? 'saveButtonSave' : 'goBackSave'
					}
					onCancel={() => setSaveSharedProject(false)}
				/>
			)}
			{showControls && (
				<Box className="project-viewer-header">
					<BackButton onClick={handleBackClick} />
					<Box className="project-viewer-header-title-box">
						<Header5 className="project-title">
							{projectTitle}
						</Header5>
					</Box>
					<Box className="project-viewer-header-actions">
						{isFeedbackButtonVisible && (
							<ContentFeedback metadata={metadata} />
						)}
						<ProjectMoreOptions
							project={project}
							map={map}
							onSave={openSaveDialog}
							onSaveAs={openSaveAsDialog}
							onHandIn={() => setAssignmentDialogOpen(true)}
							onShare={openShareDialog}
						/>
						{isSafeExamBrowser ||
						(!isProjectOwner && project.id) ? (
							<Box />
						) : (
							<Button
								variant="contained"
								color="primary"
								onClick={openSaveDialog}
							>
								<Save />
								{t('general:save')}
							</Button>
						)}
					</Box>
				</Box>
			)}
			<Box className="project-viewer-container">
				<Box className="project-viewer-map-viewer">
					<LoadingContainer sections={['map']}>
						{map && (
							<>
								<ErrorBoundary>
									<MapViewer
										mapId={map.id}
										casperId={map.casperId as string}
										corner={convertToLatLng(map.corner)}
										oppositeCorner={convertToLatLng(
											map.oppositeCorner
										)}
										hideMap={googleMapsActive}
										projection={map.projection}
									>
										<MapViewerControls
											prefix={extractPrefix(
												map.casperId as string
											)}
											mapId={map.id}
											projectTitle={projectTitle}
											corner={convertToLatLng(map.corner)}
											oppositeCorner={convertToLatLng(
												map.oppositeCorner
											)}
											attachmentCount={
												project.attachments.length
											}
											options={{
												visible: showControls,
												zoomControls: !googleMapsActive,
												legend: !googleMapsActive,
												ignoreResize: true,
												sharedProject: isSharedProject
											}}
											legendItems={legendItems}
											legendUrl={map.legendUrl}
										/>

										<MapDrawing
											removePinLocation={
												removePinLocation
											}
											drawings={project.drawings}
											attachments={
												attachmentsWithLocation
											}
											showDrawings={
												showControls &&
												!googleMapsActive
											}
											hideAttachments={
												!showAttachmentPanel
											}
										/>
										{!showControls && (
											<AttachmentLocationChanger
												onSave={moveAttachment}
											/>
										)}
										{showTopographicPin && (
											<LeafletIconPin
												position={
													topographicLocation as LatLng
												}
												icon={<MyLocation />}
											>
												<LeafletTooltip
													className="topographic-location"
													direction="top"
												>
													<Header6>
														{topographicName}
													</Header6>
												</LeafletTooltip>
											</LeafletIconPin>
										)}
										<GoogleMapsViewer
											mapId={map.id}
											hidden={!googleMapsActive}
											corner={convertToLatLng(map.corner)}
											oppositeCorner={convertToLatLng(
												map.oppositeCorner
											)}
										/>
									</MapViewer>
								</ErrorBoundary>
							</>
						)}
					</LoadingContainer>
				</Box>
				{showAttachmentPanel && (
					<Box className="attachement-panel">
						<MapAttachementSidePanel
							allowAuthorContentEditing={false}
							attachments={project.attachments}
							reorderAttachments={updateAttachments}
							onDeleteAttachment={removeAttachment}
						/>
					</Box>
				)}
			</Box>
			<MapAttachmentEditDialog
				mapId={map.id}
				casperId={map.casperId as string}
				corner={convertToLatLng(map.corner)}
				oppositeCorner={convertToLatLng(map.oppositeCorner)}
				projection={map.projection}
				attachmentCount={project.attachments.length}
				addAttachment={addAttachment}
				editAttachment={updateAttachment}
			/>
			{saveDialogState.open && <ProjectEditDialog {...saveDialogState} />}
			<AssignmentDialog
				open={assignmentDialogOpen}
				shouldSave={project.id === undefined || isProjectDirty}
				objectDescriptor="map"
				onSave={openSaveDialog}
				onClose={() => setAssignmentDialogOpen(false)}
			/>
			<ShareDialog
				{...shareDialogState}
				shouldSave={project.id === undefined || isProjectDirty}
			/>
		</Box>
	);
}

function mapStateToProps(state: AppState) {
	const showControls: boolean =
		state.attachmentsEditor.currentAttachment === undefined;

	return {
		googleMapsActive:
			state.mapViewer.panels.get(MapViewerPanel.GoogleMaps) || false,
		showControls,
		showAttachmentPanel:
			showControls &&
			(state.mapViewer.panels.get(MapViewerPanel.Attachement) || false),
		isProjectDirty: state.project.present.isDirty,
		sharedProjects: state.bosatlas.sharedProjects
	};
}

function mapDispatchToProps(
	dispatch: AppThunkDispatch<
		MapAction | ProjectActions | MapViewerActions | HttpAction
	>
) {
	return {
		fetchBosatlas: () => dispatch(fetchBosatlas()),
		addAttachment: (attachment: Attachment, file?: File) =>
			dispatch(projectAddAttachment(attachment, file)),
		updateAttachment: (attachment: Attachment, file?: File) =>
			dispatch(projectUpdateAttachment(attachment, file)),
		updateAttachments: (attachments: Attachment[]) =>
			dispatch(projectUpdateAttachments(attachments)),
		removeAttachment: (attachment: Attachment) =>
			dispatch(projectRemoveAttachment(attachment)),
		moveAttachment: (attachment: Attachment) =>
			dispatch(projectMoveAttachment(attachment)),
		createProject: (project: Project, brandName: string) =>
			dispatch(projectCreate(project, brandName)),
		updateProject: (project: Project) => dispatch(projectUpdate(project)),
		copyProject: (project: Project, brandName: string) =>
			dispatch(projectCopy(project, brandName)),
		clearActionPanels: () => dispatch(mapViewerClearActionPanels()),
		addToLastViewed: (mapId: string, mapUrl: string, title: string) =>
			dispatch(mapViewerAddToLastViewed(mapId, mapUrl, title)),
		toggleGoogleMaps: () =>
			dispatch(mapViewerSwitchActionPanel(MapViewerPanel.GoogleMaps)),
		undo: () => dispatch(projectUndo()),
		redo: () => dispatch(projectRedo()),
		setPreserveSearchState: () => dispatch(searchSetPreserveState()),
		clearSearchState: () => dispatch(searchClear()),
		shareProject: (groups: UserGroupDto[], project: Project) =>
			dispatch(shareProjectWithGroup(groups, project))
	};
}

export const ProjectViewer = connect(
	mapStateToProps,
	mapDispatchToProps
)(ProjectViewerView);
